// The answer panel for one issue (E32-S8-T4; mockups/issues-queue.html, "the answer panel"): the
// question, the facts, what it holds up, then — for the client reviewer — the three ways to answer
// and the waiver; for the guide's owners the same facts, read only, and a follow-up question on a
// closed issue. The form an issue opens on follows its kind (src/issues/model.ts defaultMode).
//
// An act's answer from the API is the issue as it now stands and goes into the cached queue, so the
// panel redraws from it. A 409 means someone else got there first: the queue is fetched again and a
// line says so. A 400 names the field; a 403 means the role cannot act. The API decides all of it;
// the checks here only save a round trip for an empty field.
import type { SourceIssue, SourceIssueAnswer } from "@waypoint/contracts";
import { Button, cn } from "@waypoint/surface";
import { useRef, useState, type ReactNode } from "react";
import { ConflictError, ForbiddenError, ValidationError } from "../../api/errors.js";
import { t } from "../../i18n/t.js";
import {
  citedPages,
  defaultMode,
  holdsRender,
  isBlocking,
  modeAvailable,
  pagesList,
  provisionalPages,
  type Abilities,
  type AnswerMode,
} from "../../issues/model.js";
import { useActOnIssue, type IssueAct } from "../../issues/queries.js";
import { FieldError, Label, TextArea } from "../../ui/form.js";
import {
  answerText,
  formatWhen,
  guideName,
  IssueChips,
  pagesLabel,
  pagesText,
  SegmentId,
} from "./issue-format.js";

export interface ActedOn {
  readonly act: IssueAct["act"];
  readonly issue: SourceIssue;
}

interface Props {
  readonly packId: string;
  readonly issue: SourceIssue;
  readonly abilities: Abilities;
  /** A segment's title from the production-plan catalogue; undefined where it has none. */
  readonly titleOf: (segmentId: string) => string | undefined;
  /** Called after the API accepted an act: the page announces it and updates its counts. */
  readonly onActed: (done: ActedOn) => void;
  /** The "Next open issue" control; absent when there is none to go to. */
  readonly onNextOpen?: (() => void) | undefined;
  /** The narrow layout stacks the panel under the list and offers a way back. */
  readonly onBack: () => void;
}

/** Where an error shows: the answer form, the waiver, the follow-up, or the panel as a whole. */
type Scope = "answer" | "waive" | "reopen" | "panel";

interface Problem {
  readonly scope: Scope;
  readonly message: string;
  /** The id of the control to put the keyboard on. */
  readonly focus?: string | undefined;
}

const FIELD_FOR: Readonly<Record<string, { scope: Scope; focus: string }>> = {
  "body.page": { scope: "answer", focus: "page-first" },
  "body.text": { scope: "answer", focus: "answer-text" },
  "body.value": { scope: "answer", focus: "answer-confirm" },
  "body.reason": { scope: "waive", focus: "waive-reason" },
  "body.question": { scope: "reopen", focus: "reopen-question" },
};

/** What the API's refusal means on this screen. */
export function problemFrom(err: unknown, act: IssueAct["act"]): Problem {
  if (err instanceof ConflictError) return { scope: "panel", message: t("issues.error.conflict") };
  if (err instanceof ForbiddenError)
    return { scope: "panel", message: t("issues.error.forbidden") };
  if (err instanceof ValidationError && err.source === "request") {
    const field = err.fields.map((f) => FIELD_FOR[f]).find((f) => f !== undefined);
    const scope: Scope = act === "answer" ? "answer" : act === "waive" ? "waive" : "reopen";
    if (field !== undefined)
      return {
        ...field,
        message: err.fields.includes("body.page")
          ? t("issues.error.page")
          : t("issues.error.field"),
      };
    return { scope, message: t("issues.error.field") };
  }
  return { scope: "panel", message: t("issues.error.failed") };
}

function Facts({
  issue,
  titleOf,
}: {
  issue: SourceIssue;
  titleOf: (segmentId: string) => string | undefined;
}) {
  const dt = "text-11 font-semibold tracking-[0.08em] text-wp-text-3 uppercase";
  return (
    <dl className="m-0 grid gap-2 text-13">
      <div>
        <dt className={dt}>{t("issues.meta.guide")}</dt>
        <dd className="m-0 mt-0.5">{guideName(issue.guide)}</dd>
      </div>
      <div>
        <dt className={dt}>{t("issues.panel.printed")}</dt>
        <dd className="m-0 mt-0.5">{pagesText(issue)}</dd>
      </div>
      <div>
        <dt className={dt}>{t("issues.panel.segments")}</dt>
        <dd className="m-0 mt-0.5">
          {issue.segments.length > 0 ? (
            <ul className="m-0 grid list-none gap-1.5 p-0">
              {issue.segments.map((id) => (
                <li key={id}>
                  <SegmentId id={id} title={titleOf(id)} />
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-wp-text-3">{t("issues.noSegment")}</span>
          )}
        </dd>
      </div>
    </dl>
  );
}

function Callout({
  tone,
  title,
  children,
}: {
  tone: "plain" | "caution" | "ask";
  title: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-8 border px-3 py-2.5 text-13",
        tone === "plain" && "border-wp-line-strong bg-wp-bg/58 text-wp-text-2",
        tone === "caution" && "border-caution/48 bg-caution/11 text-wp-text",
        tone === "ask" && "border-wp-accent/44 bg-wp-accent/13 text-wp-text",
      )}
    >
      <b className="mb-0.5 block">{title}</b>
      {children}
    </div>
  );
}

function Gate({ issue, abilities }: { issue: SourceIssue; abilities: Abilities }) {
  if (holdsRender(issue))
    return (
      <Callout
        tone="caution"
        title={t("issues.gate.holds", { segments: issue.segments.join(", ") })}
      >
        {t(abilities.answer ? "issues.gate.holdsLine" : "issues.gate.holdsReadOnly")}
      </Callout>
    );
  if (isBlocking(issue))
    return (
      <Callout tone="caution" title={t("issues.gate.noneHeld")}>
        {t("issues.gate.noneHeldLine")}
      </Callout>
    );
  if (issue.status === "open")
    return (
      <Callout tone="plain" title={t("issues.gate.open")}>
        {t("issues.gate.openLine")}
      </Callout>
    );
  return null;
}

const OVERLINE = "text-11 font-semibold tracking-[0.08em] text-wp-text-3 uppercase";

/** The closed states: who answered or waived, and when, and what it means for the renders. */
function Closed({ issue }: { issue: SourceIssue }) {
  if (issue.status === "answered" && issue.answer && issue.answeredBy && issue.answeredAt)
    return (
      <div className="grid gap-1.5 rounded-12 border border-wp-accent/44 bg-wp-accent/13 px-3.5 py-3">
        <div className={OVERLINE}>{t("issues.done.answered")}</div>
        <blockquote className="m-0 border-l-2 border-wp-accent pl-2.5 text-14">
          {answerText(issue.answer)}
        </blockquote>
        <p className="m-0 text-13 text-wp-text-2">
          {t("issues.done.by", { by: issue.answeredBy, at: formatWhen(issue.answeredAt) })}
        </p>
        {issue.kind === "contradiction" && issue.segments.length > 0 ? (
          <p className="m-0 text-13 text-wp-text-2">
            {t("issues.done.backToReview", { segments: issue.segments.join(", ") })}
          </p>
        ) : null}
        <p className="m-0 text-13 text-wp-text-2">{t("issues.done.changeNote")}</p>
      </div>
    );
  if (issue.status === "waived" && issue.waiver)
    return (
      <div className="grid gap-1.5 rounded-12 border border-wp-line-strong bg-wp-bg/58 px-3.5 py-3">
        <div className={OVERLINE}>{t("issues.done.waived")}</div>
        <blockquote className="m-0 border-l-2 border-wp-accent pl-2.5 text-14">
          {issue.waiver.reason}
        </blockquote>
        <p className="m-0 text-13 text-wp-text-2">
          {t("issues.done.by", {
            by: issue.waiver.waivedBy,
            at: formatWhen(issue.waiver.waivedAt),
          })}{" "}
          {t("issues.done.waivedLine")}
        </p>
      </div>
    );
  return null;
}

function Option({
  mode,
  current,
  disabled,
  title,
  hint,
  onPick,
  children,
}: {
  mode: AnswerMode;
  current: AnswerMode;
  disabled: boolean;
  title: string;
  hint: string;
  onPick: (mode: AnswerMode) => void;
  children?: ReactNode;
}) {
  const on = current === mode;
  return (
    <div
      data-on={on}
      className={cn(
        "rounded-12 border bg-wp-panel/74",
        on ? "border-wp-accent" : "border-wp-line",
        disabled && "opacity-60",
      )}
    >
      <label
        className={cn(
          "grid grid-cols-[auto_1fr] items-start gap-x-2.5 gap-y-0.5 px-3 py-2.5",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
        )}
      >
        <input
          type="radio"
          name="mode"
          value={mode}
          checked={on}
          disabled={disabled}
          onChange={() => {
            onPick(mode);
          }}
          className="mt-0.75 size-4 accent-wp-accent"
        />
        <span className="text-14 font-semibold">{title}</span>
        <span className="col-start-2 text-12 text-wp-text-3">{hint}</span>
      </label>
      {on ? <div className="grid gap-2 pr-3 pb-3 pl-9.5">{children}</div> : null}
    </div>
  );
}

function AnswerForm({
  issue,
  busy,
  problem,
  onSubmit,
}: {
  issue: SourceIssue;
  busy: boolean;
  problem: Problem | undefined;
  onSubmit: (answer: SourceIssueAnswer | Problem) => void;
}) {
  const [mode, setMode] = useState<AnswerMode>(() => defaultMode(issue));
  const [page, setPage] = useState("");
  const [text, setText] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const cited = citedPages(issue);
  const inferred = provisionalPages(issue);
  const inferredPages = pagesList(inferred);
  const error = problem?.scope === "answer" ? problem : undefined;

  /** The answer the form holds, or the problem with it. */
  const answered = (): SourceIssueAnswer | Problem => {
    if (mode === "reading") {
      if (page === "")
        return {
          scope: "answer",
          message: t("issues.error.required.reading"),
          focus: "page-first",
        };
      return { form: "reading", page };
    }
    if (mode === "text") {
      if (text.trim() === "")
        return { scope: "answer", message: t("issues.error.required.text"), focus: "answer-text" };
      return { form: "text", text: text.trim() };
    }
    if (!confirmed)
      return {
        scope: "answer",
        message: t("issues.error.required.confirm"),
        focus: "answer-confirm",
      };
    return { form: "confirm", value: t("issues.form.inferredValue", { pages: inferredPages }) };
  };

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(answered());
      }}
    >
      <fieldset className="m-0 grid min-w-0 gap-2 border-0 p-0">
        <legend className="mb-2 p-0 text-14 font-semibold">{t("issues.form.legend")}</legend>
        <Option
          mode="reading"
          current={mode}
          disabled={!modeAvailable(issue, "reading")}
          title={t("issues.form.reading")}
          hint={t(cited.length > 0 ? "issues.form.readingHint" : "issues.form.readingNone")}
          onPick={setMode}
        >
          <fieldset className="m-0 min-w-0 border-0 p-0">
            <legend className="mb-1 p-0 text-12 font-semibold text-wp-text-2">
              {t("issues.form.readingWhich")}
            </legend>
            <ul className="m-0 grid list-none gap-1.5 p-0">
              {cited.map((ref, index) => (
                <li key={ref}>
                  <label
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-8 border bg-wp-bg/58 px-2 py-1.5 text-14",
                      page === ref ? "border-wp-accent" : "border-wp-line",
                    )}
                  >
                    <input
                      type="radio"
                      name="page"
                      value={ref}
                      id={index === 0 ? "page-first" : undefined}
                      checked={page === ref}
                      aria-describedby={error ? "answer-error" : undefined}
                      onChange={() => {
                        setPage(ref);
                      }}
                      className="accent-wp-accent"
                    />
                    {pagesLabel([ref])}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        </Option>
        <Option
          mode="text"
          current={mode}
          disabled={false}
          title={t("issues.form.text")}
          hint={t("issues.form.textHint")}
          onPick={setMode}
        >
          <div>
            <Label htmlFor="answer-text">{t("issues.form.textLabel")}</Label>
            <TextArea
              id="answer-text"
              rows={4}
              value={text}
              aria-invalid={error?.focus === "answer-text"}
              aria-describedby={error ? "answer-error" : undefined}
              onChange={(e) => {
                setText(e.target.value);
              }}
            />
          </div>
        </Option>
        <Option
          mode="confirm"
          current={mode}
          disabled={!modeAvailable(issue, "confirm")}
          title={
            inferred.length > 0
              ? t("issues.form.confirm", { pages: inferredPages })
              : t("issues.form.confirmGeneric")
          }
          hint={t(inferred.length > 0 ? "issues.form.confirmHint" : "issues.form.confirmNone")}
          onPick={setMode}
        >
          <div className={OVERLINE}>{t("issues.form.inferred")}</div>
          <div className="rounded-8 border border-caution/48 bg-wp-bg/58 px-2.5 py-2 font-mono text-16 font-semibold">
            {t("issues.form.inferredValue", { pages: inferredPages })}
          </div>
          <p className="m-0 text-13 text-wp-text-2">{t("issues.form.inferredNote")}</p>
          <label className="flex cursor-pointer items-start gap-2 text-14">
            <input
              type="checkbox"
              id="answer-confirm"
              checked={confirmed}
              aria-describedby={error ? "answer-error" : undefined}
              onChange={(e) => {
                setConfirmed(e.target.checked);
              }}
              className="mt-0.75 accent-wp-accent"
            />
            {t("issues.form.confirmBox")}
          </label>
        </Option>
      </fieldset>
      <FieldError id="answer-error">{error?.message}</FieldError>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? t("issues.form.saving") : t("issues.form.save")}
        </Button>
      </div>
    </form>
  );
}

function Waive({
  busy,
  problem,
  onWaive,
}: {
  busy: boolean;
  problem: Problem | undefined;
  onWaive: (reason: string | Problem) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const toggle = useRef<HTMLButtonElement>(null);
  const error = problem?.scope === "waive" ? problem : undefined;
  return (
    <div className="grid gap-2 border-t border-wp-line pt-3">
      <div>
        <Button
          ref={toggle}
          type="button"
          variant="secondary"
          aria-expanded={open}
          aria-controls="waive-form"
          onClick={() => {
            setOpen(!open);
          }}
        >
          {t("issues.waive.open")}
        </Button>
      </div>
      {open ? (
        <form
          id="waive-form"
          noValidate
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            onWaive(
              reason.trim() === ""
                ? {
                    scope: "waive",
                    message: t("issues.error.required.reason"),
                    focus: "waive-reason",
                  }
                : reason.trim(),
            );
          }}
        >
          <div>
            <Label htmlFor="waive-reason">{t("issues.waive.label")}</Label>
            <TextArea
              id="waive-reason"
              rows={3}
              autoFocus
              value={reason}
              aria-invalid={error !== undefined}
              aria-describedby="waive-error"
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
            <FieldError id="waive-error">{error?.message}</FieldError>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" variant="secondary" disabled={busy}>
              {t("issues.waive.go")}
            </Button>
            <Button
              type="button"
              variant="quiet"
              onClick={() => {
                setOpen(false);
                toggle.current?.focus();
              }}
            >
              {t("issues.waive.cancel")}
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}

function Reopen({
  busy,
  problem,
  onReopen,
}: {
  busy: boolean;
  problem: Problem | undefined;
  onReopen: (question: string | Problem) => void;
}) {
  const [question, setQuestion] = useState("");
  const error = problem?.scope === "reopen" ? problem : undefined;
  return (
    <form
      noValidate
      className="grid gap-2 border-t border-wp-line pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        onReopen(
          question.trim() === ""
            ? {
                scope: "reopen",
                message: t("issues.error.required.question"),
                focus: "reopen-question",
              }
            : question.trim(),
        );
      }}
    >
      <div>
        <Label htmlFor="reopen-question">{t("issues.reopen.label")}</Label>
        <TextArea
          id="reopen-question"
          rows={3}
          value={question}
          aria-invalid={error !== undefined}
          aria-describedby="reopen-hint reopen-error"
          onChange={(e) => {
            setQuestion(e.target.value);
          }}
        />
        <p id="reopen-hint" className="m-0 mt-1 text-12 text-wp-text-3">
          {t("issues.reopen.hint")}
        </p>
        <FieldError id="reopen-error">{error?.message}</FieldError>
      </div>
      <div>
        <Button type="submit" variant="secondary" disabled={busy}>
          {t("issues.reopen.go")}
        </Button>
      </div>
    </form>
  );
}

export function AnswerPanel({
  packId,
  issue,
  abilities,
  titleOf,
  onActed,
  onNextOpen,
  onBack,
}: Props) {
  const act = useActOnIssue(packId);
  const [problem, setProblem] = useState<Problem>();
  const heading = useRef<HTMLHeadingElement>(null);

  const show = (p: Problem) => {
    setProblem(p);
    if (p.focus !== undefined)
      requestAnimationFrame(() => document.getElementById(p.focus ?? "")?.focus());
  };

  const run = (request: IssueAct) => {
    setProblem(undefined);
    act.mutate(
      { issueId: issue.id, act: request },
      {
        onSuccess: (updated) => {
          onActed({ act: request.act, issue: updated });
          heading.current?.focus();
        },
        onError: (err) => {
          show(problemFrom(err, request.act));
        },
      },
    );
  };

  const isProblem = (x: unknown): x is Problem =>
    typeof x === "object" && x !== null && "scope" in x;
  const open = issue.status === "open";

  return (
    <>
      <div className="flex items-center gap-2.5 px-4 pt-3.5">
        <h2
          id="panel-heading"
          ref={heading}
          tabIndex={-1}
          className="min-w-0 flex-1 text-18 font-semibold leading-tight outline-none"
        >
          {issue.id}
        </h2>
        <Button type="button" variant="quiet" className="min-[1060px]:hidden" onClick={onBack}>
          {t("issues.panel.back")}
        </Button>
      </div>
      <div className="grid gap-3.5 px-4 pt-3.5 pb-4">
        <div className="flex flex-wrap gap-1.5">
          <IssueChips issue={issue} />
        </div>
        <div>
          <h3 className={cn(OVERLINE, "mb-1")}>{t("issues.panel.question")}</h3>
          <p className="m-0 text-15">{issue.summary}</p>
        </div>
        <Facts issue={issue} titleOf={titleOf} />
        <Gate issue={issue} abilities={abilities} />
        {issue.followUps.map((f) => (
          <Callout key={f.askedAt} tone="ask" title={t("issues.followUp.title")}>
            {f.question}
            <div className="mt-1 text-13 text-wp-text-2">
              {t("issues.followUp.by", { by: f.askedBy, at: formatWhen(f.askedAt) })}
            </div>
          </Callout>
        ))}
        {problem?.scope === "panel" ? (
          <FieldError id="panel-error">{problem.message}</FieldError>
        ) : null}
        {open && abilities.answer ? (
          <>
            <AnswerForm
              key={issue.id}
              issue={issue}
              busy={act.isPending}
              problem={problem}
              onSubmit={(answer) => {
                if (isProblem(answer)) show(answer);
                else run({ act: "answer", answer });
              }}
            />
            <Waive
              busy={act.isPending}
              problem={problem}
              onWaive={(reason) => {
                if (isProblem(reason)) show(reason);
                else run({ act: "waive", reason });
              }}
            />
            <p className="m-0 text-13 text-wp-text-2">{t("issues.form.recordedAs")}</p>
          </>
        ) : null}
        {!open ? <Closed issue={issue} /> : null}
        {!abilities.answer ? (
          <Callout tone="plain" title={t("issues.readOnly.title")}>
            {t("issues.readOnly.line")}
          </Callout>
        ) : null}
        {!open && abilities.reopen ? (
          <Reopen
            busy={act.isPending}
            problem={problem}
            onReopen={(question) => {
              if (isProblem(question)) show(question);
              else run({ act: "reopen", question });
            }}
          />
        ) : null}
        {!open && onNextOpen ? (
          <div>
            <Button type="button" variant="secondary" onClick={onNextOpen}>
              {t("issues.panel.nextOpen")}
            </Button>
          </div>
        ) : null}
      </div>
    </>
  );
}
